import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { IonicModule, IonSelect } from '@ionic/angular'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { NgForOf, NgIf } from '@angular/common'
import copy from 'fast-copy'

export type ColumnDef = {
  id: string
  name: string
}

export type ColumnLayout = string[]

@Component({
  selector: 'app-column-select',
  templateUrl: './column-select.component.html',
  styleUrls: ['./column-select.component.scss'],
  imports: [IonicModule, FormsModule, NgForOf, NgIf],
  host: {
    '[class.disabled]': 'disabled',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColumnSelectComponent),
      multi: true,
    },
  ],
})
export class ColumnSelectComponent implements OnChanges, AfterViewInit, ControlValueAccessor {
  @ViewChildren('columnSelect') selects: QueryList<IonSelect>

  @Input() value: ColumnLayout = []
  @Input() columns: ColumnDef[] = []
  @Input() allowEmpty = true
  @Input() disabled: boolean = false

  @Output() changed = new EventEmitter<ColumnLayout>()

  protected onChange = (_: any) => {}
  protected onTouched = (_: any) => {}

  protected selectedValues: ColumnLayout = []
  protected lastValue: ColumnLayout = []

  constructor() {}

  ngAfterViewInit() {
    this.renderValue()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('value')) {
      this.renderValue()
    }
  }

  writeValue(value: ColumnLayout) {
    // Ensure value is always an array to prevent rendering issues
    const safeValue = Array.isArray(value) ? value : []
    this.selectedValues = safeValue ? copy(safeValue) : []
  }

  registerOnChange(fn: (_: any) => void) {
    this.onChange = fn
  }

  registerOnTouched(fn: (_: any) => void) {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled
  }

  /**
   * Projects component's current value into UI controls.
   */
  renderValue() {
    if (!this.selects) {
      return
    }

    // Ensure value is always an array
    const safeValue = Array.isArray(this.value) ? this.value : []

    try {
      for (let i = 0; i < this.selects.length; i++) {
        if (safeValue[i] !== undefined) {
          this.selectedValues[i] = safeValue[i]
        }
      }
      this.lastValue = copy(safeValue)
    } catch (error) {
      console.error('Error rendering column select values:', error)
      // Fallback to empty array if rendering fails
      this.selectedValues = []
      this.lastValue = []
    }
  }

  /**
   * Called when a column dropdown is changed.
   * @param event
   */
  protected columnChanged(event) {
    try {
      const changedSelect = event.target
      const changedIndex = +changedSelect.dataset.index

      for (let i = 0; i < this.selects.length; i++) {
        const select = this.selects.get(i)
        if (select && select.name !== changedSelect.name && select.value === changedSelect.value) {
          this.selectedValues[i] = this.lastValue[changedIndex]
          break
        }
      }

      this.lastValue = copy(this.selectedValues)
      this.changed.emit(this.selectedValues)
      this.onChange(this.selectedValues)
    } catch (error) {
      console.error('Error handling column change:', error)
    }
  }
}
